<template>
  <div
    class="w-full grid grid-cols-1 grid-rows-2 xl:flex justify-between items-center gap-x-2"
  >
    <div class="col-span-3 xl:col-span-1">
      <NuxtLink to="/" class="flex pt-2 pb-5 w-fit" external no-prefetch>
        <BenchmarkLogo class="w-[226px] h-[34px]" />
      </NuxtLink>
    </div>

    <div class="flex items-center gap-x-1 h-full justify-end w-full xl:w-fit">
      <NavigationItem
        v-for="(menu, index) in DESKTOP_MENU"
        :key="getKey(menu)"
        :data="menu"
        :is-selected-group="activeMenuIndex === index"
        data-testid="top-navigation-item"
      />
      <a
        :href="$config.public.sourceUrl"
        class="relative cursor-pointer select-none items-center text-sm xl:text-base py-4 xl:py-[22px] px-2 border-b-4 border-transparent h-full font-bold"
        aria-label="Open Benchmark Source website"
      >
        <span
          class="text-s-900 font-bold transition-all duration-300 group-hover:text-opacity-60 leading-none"
          data-testid="navigation-menu"
        >
          Source
        </span>
      </a>
    </div>
    <div class="flex gap-x-3 justify-end">
      <ButtonContactUs class="py-2 px-4 leading-none" />
    </div>
  </div>
</template>

<script setup>
/** packages */
import { ref, onMounted, watch } from 'vue'

/** components */
import NavigationItem from '~/components/navigation/Item.vue'
import ButtonContactUs from '~/components/shared/common/ButtonContactUs.vue'
import BenchmarkLogo from '~/assets/icons/benchmark-logo.svg'

/** constants */
import { DESKTOP_MENU } from '~/constants/main-menu'

/** stores */
import { useMenuStore } from '~/stores/menu'
import { useBreadcrumbStore } from '~/stores/breadcrumb'

const route = useRoute()
const menuStore = useMenuStore()
const breadcrumbStore = useBreadcrumbStore()
const { $bmAuth } = useNuxtApp()
const { $config } = useNuxtApp()

const activeMenuIndex = ref(undefined)

onMounted(() => {
  activeMenuIndex.value = calculateActiveMenuIndex(route.fullPath, DESKTOP_MENU)
})

watch(
  () => route.fullPath,
  (newPath) => {
    activeMenuIndex.value = calculateActiveMenuIndex(newPath, DESKTOP_MENU)
  },
  { immediate: true, deep: true },
)

/**
 * Calculate the active menu index based on the current path
 * This function is extracted to avoid duplicating logic and to make it testable
 */
function calculateActiveMenuIndex(fullPath, menuList) {
  if (fullPath === '/') return undefined

  const index = menuList.findIndex((item) => {
    if (item.groups.length === 0) return item.href === fullPath

    const paths = fullPath.split('/').filter((path) => path)

    return item.groups
      .filter(
        (group) =>
          (group.href && paths.includes(group.href.replace('/', ''))) ||
          group.children?.length > 0,
      )
      .map((group) => group.children)
      .map((children) =>
        children.map((child) => child?.children ?? child).flat(),
      )
      .flat()
      .some((subItem) => {
        const market = subItem?.href?.trim()?.split('/')?.at(1)
        return paths.includes(market)
      })
  })

  breadcrumbStore.clearBreadcrumb()
  if (index >= 0) getActiveSubItem(menuList[index])

  return index
}

function getActiveSubItem(menu) {
  if (menu.name?.toLowerCase() !== 'markets') {
    breadcrumbStore.addItemBreadcrumb(menu.name)
  }

  if (menu.groups.length === 0) return

  const activeSubMenu = menuStore.getActiveSubMenu
  const activeMenu = menuStore.activeMenu

  if (activeMenu !== null) {
    breadcrumbStore.addItemBreadcrumb(activeMenu.label)
  }

  if (!activeSubMenu) return
  if (!(!$bmAuth.isAuthenticated() && activeSubMenu === 'Chart builder')) {
    breadcrumbStore.addItemBreadcrumb(activeSubMenu)
  }
}

function getKey(menu) {
  if (!menu.groups.length) return menu.name

  const childrenLength = menu.groups.flatMap((group) => group.children).length

  return `${menu.name}_${childrenLength}`
}
</script>
